import { html, View, on } from 'rune-ts';
import { makeEmptyScreenNavigate } from '../../../../shared/app/navigate';
import { dataStr } from '../../../../shared/util/dataStr';
import klass from './PopupStoreVisualCard.module.scss';
import { staticTypo, staticTypoPcMo, typo, typoPcMo } from '../../../../shared/typography/typo';
import { PopupStoreItemCardType, PopupStoreListType } from '../../../../features/PopupStores/type';
import { classes, htmlIf } from '../../../../shared/util';
import { PopupStoreProgress, Timer } from '../PopupStoreProgress/PopupStoreProgress';
import { OpenSubscribeModalEvent } from '../../../../features/PopupStores/service/events';
import { format } from 'date-fns';
import { getResizedUrl } from '../../../../shared/util/image';

export interface PopupStoreVisualCardProps {
  item: PopupStoreItemCardType;
  type?: PopupStoreListType;
  is_mobile: boolean;
}

export class PopupStoreVisualCard extends View<PopupStoreVisualCardProps> {
  protected override template() {
    const { type = 'now', is_mobile } = this.data;
    const parsed_item = this._parseItem();

    return html`
      <div class="${klass.visual_card} ${classes({ [klass.upcoming]: type === 'upcoming' })}">
        <div class="${klass.image}">
          <img
            src="${getResizedUrl({
              url: parsed_item.thumbnail_url,
              width: 1920 * 2,
              format: 'jpeg',
              quality: 85,
            })}"
            alt="팝업스토어 썸네일"
          />
        </div>

        <${parsed_item.tag} ${parsed_item.post_message} ${parsed_item.href} class="${klass.wrapper}">
          <div class="${klass.meta}">
            <!--  카드 상단 뱃지, now 에서만 노출   -->
            ${htmlIf(
              html`<div
                class="${klass.badges} ${staticTypoPcMo({
                  is_mobile,
                  pc: 'bebas_24_bold',
                  mo: 'bebas_16_bold',
                })}"
              >
                <p class="${klass.badge}"># ${parsed_item.numbering}</p>
                <p class="${klass.badge}">${html.preventEscape(parsed_item.creator_name)}</p>
              </div>`,
              type === 'now',
            )}

            <!--  타이틀  -->
            <h3 class="${klass.title} ${typoPcMo({ is_mobile, pc: '48_bold', mo: '24_bold' })}">
              ${html.preventEscape(parsed_item.title)}
            </h3>

            <!--  디스크립션, now && pc 에서만 노출  -->
            ${htmlIf(
              html`<h4 class="${klass.description} ${typo('16_medium')}">
                ${html.preventEscape(parsed_item.description)}
              </h4>`,
              type === 'now' && !is_mobile,
            )}
            
            <!--  see more, now && mobile 에서만 노출  -->
            ${htmlIf(
              html`<div class="${klass.see_more} ${typo('14_medium')}">See More</div>`,
              type === 'now' && is_mobile,
            )}
          </div>
          
          <!--  상태 표시, now에서만 노출  -->
          ${htmlIf(this._makeStatusHtml(), type === 'now')}

          <!--  오픈런, upcoming 에서만 노출  -->
          ${htmlIf(this._makeSubscribeHtml(), type === 'upcoming')}
        </${parsed_item.tag}>
      </div>
    `;
  }

  private _parseItem() {
    const { item, type = 'now', is_mobile } = this.data;

    const tag = type === 'upcoming' ? 'div' : 'a';
    const href = type === 'upcoming' ? '' : `href=${`/${ET.lang}/@/popup-store/${item.id}`}`;
    const post_message =
      type === 'upcoming'
        ? ''
        : html`data-post-message=${dataStr(makeEmptyScreenNavigate(`/${ET.lang}/@/popup-store/${item.id}`))}`;

    const thumbnail_url = is_mobile ? item.mobile_thumbnail || item.list_thumbnail : item.thumbnail;
    const title = (is_mobile ? item.mobile_title || item.title : item.title).replaceAll('\n', '<br/>');
    const creator_name = type === 'now' ? item.creator_name_en || item.creator_name : item.creator_name;
    const description = (item.description || '').replaceAll('\n', '<br/>');

    return {
      ...item,
      description,
      tag,
      href,
      post_message,
      thumbnail_url,
      title,
      creator_name,
    };
  }

  private _makeSubscribeHtml() {
    const { item, is_mobile, type } = this.data;
    const timer_font = classes({
      [staticTypo('bebas_96_bold')]: !is_mobile,
      [staticTypo('bebas_48_bold')]: is_mobile,
    });
    return html`
      <div class="${klass.upcoming}">
        <div class="${klass.remain}">
          <div class="${timer_font}">
            ${new Timer({ end_at: type === 'now' ? item.end_at : item.start_at })}
          </div>
          <div
            class="${klass.rate} ${staticTypoPcMo({ is_mobile, pc: 'unica_24_bold', mo: 'unica_20_bold' })}"
          >
            ${format(new Date(item.start_at), 'yyyy.MM.dd HH:mm')} Open
          </div>
        </div>
        <!--
        <div class="$ {klass.subscribe}">
          <button type="button" class="$ {klass.subscribe_btn} $ {typo('32_bold')}">
            <span>$ {ET('mps2::popup_store::upcoming::openrun_btn')}</span> $ {AlertIcon}
          </button>
        </div>
        -->
      </div>
    `;
  }

  private _makeStatusHtml() {
    const item = this.data.item;
    return html`
      <div class="${klass.status}">
        ${new PopupStoreProgress({
          tab: this.data.type,
          is_soldout: item.is_soldout,
          soldout_label: item.soldout_label!,
          visible: item.goal_visible,
          start_at: item.start_at,
          end_at: item.end_at,
          size: 'large',
        })}
      </div>
    `;
  }

  @on('click', `.${klass.subscribe_btn}`)
  onClickSubscribe() {
    const { item } = this.data;
    this.dispatchEvent(OpenSubscribeModalEvent, {
      detail: { item },
      bubbles: true,
    });
  }
}
